import React from "react";
import styled from "@emotion/styled";
import { withFormik } from "formik";
import { track, trackPopupEvent } from "@herb/utils/analytics";
import { Box, Button, Flex, Text } from "@herb/ui";

const ErrorText = props => (
  <Text color="error" fontSize="1" mt={1} {...props} />
);

/**
 * Verify legal age based on input value
 * @param  {object} values The finished values from the form
 * @return {boolean}       Whether the age is legal or not
 */

/**
 * Handle form submission
 * @param  {Object}   e              The event object from the submission
 * @param  {Object}   values         The values object
 * @param  {Boolean}  isValid        Whether the form is valid or not
 * @param  {Function} submitCallback The formik submit callback function
 * @param  {Function} setStatus      The formik set status function
 * @param  {Function} callback       The prop callback method
 */
const formSubmission = (
  e,
  values,
  isValid,
  submitCallback,
  setStatus,
  callback
) => {
  // Prevent default
  if (e) {
    e.preventDefault();
  }

  setStatus({
    submitted: true
  });

  if (isValid) {
    callback(values.rememberMe !== false);
  }

  // Call formik submit handler to trigger final validation
  if (submitCallback) {
    submitCallback(e);
  }
};

class AgeGateForm extends React.Component {
  render() {
    const {
      values,
      errors,
      status,
      setStatus,
      handleSubmit,
      handleChange,
      handleBlur,
      isValid,
      ipData,
      innerRef,
      callback,
      secondsOnGate = () => null,
      onAnswer = () => {}
    } = this.props;

    const formValues = {
      COUNTRY: ipData.country_code,
      ...values
    };

    const sharedProps = {
      onChange: handleChange,
      onBlur: handleBlur,
      dark: true,
      containerPx: "14px"
    };

    return (
      <form
        onSubmit={evt =>
          formSubmission(
            evt,
            formValues,
            isValid,
            handleSubmit,
            setStatus,
            callback
          )
        }
        noValidate
        action=""
      >
        <Text
          fontSize={4}
          mb={-4}
          fontWeight="300"
          fontFamily="Source Code Pro"
          textAlign="center"
          css={{
            "@media screen and (max-width:769px)": {
              fontSize: "18px",
              lineHeight: "1.35",
              marginBottom: "0"
            }
          }}
        >
          Are you 21 or older?
        </Text>
        {/* )} */}

        <Flex
          css={{
            paddingTop: "60px",
            paddingBottom: "25px",
            justifyContent: "space-evenly",
            gap: "16px",
            "@media screen and (max-width:769px)": {
              paddingTop: "24px",
              paddingBottom: "8px",
              flexDirection: "column",
              alignItems: "stretch",
              gap: "10px"
            }
          }}
        >
          <Box
            css={{
              "@media screen and (max-width:769px)": {
                paddingBottom: "15px"
              }
            }}
          >
            <Text>
              <Button
                type="button"
                px={5}
                ref={innerRef}
                size="small"
                css={t => ({
                  fontSize: "24px",
                  fontWeight: "300",
                  display: "flex",
                  justifyContent: "center",
                  paddingLeft: "43px",
                  paddingRight: "44px",
                  backgroundColor: "#FFF",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  fontFamily: "Source Code Pro",
                  fontStyle: "italic",
                  borderRadius: "0",
                  border: "1px solid #000",
                  "@media screen and (max-width:769px)": {
                    width: "100%",
                    minHeight: "46px",
                    fontSize: "16px",
                    paddingLeft: "18px",
                    paddingRight: "18px"
                  }
                })}
                onClick={() => {
                  track("Submit Form", {
                    form: "Age Gate",
                    ofAge: false,
                    rememberMe: values.rememberMe !== false,
                    values: formValues
                  });
                  onAnswer();
                  trackPopupEvent("age_gate_decline", {
                    seconds_on_gate: secondsOnGate()
                  });

                  if (typeof window !== "undefined") {
                    window.location.href =
                      "https://www.google.com/search?q=herb";
                  }
                }}
              >
                no
              </Button>
            </Text>
          </Box>
          <Box>
            <Button
              type="submit"
              px={5}
              ref={innerRef}
              size="small"
              css={t => ({
                fontSize: "24px",
                fontWeight: "300",
                display: "flex",
                justifyContent: "center",
                paddingLeft: "50px",
                paddingRight: "50px",
                backgroundColor: "#3041FF",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontFamily: "Source Code Pro",
                fontStyle: "italic",
                borderRadius: "0",
                "@media screen and (max-width:769px)": {
                  width: "100%",
                  minHeight: "48px",
                  fontSize: "17px",
                  paddingLeft: "18px",
                  paddingRight: "18px"
                }
              })}
              onClick={() => {
                track("Submit Form", {
                  form: "Age Gate",
                  ofAge: isValid,
                  rememberMe: values.rememberMe !== false,
                  values: formValues
                });
                onAnswer();
                trackPopupEvent("age_gate_accept", {
                  remember_me: values.rememberMe !== false,
                  seconds_on_gate: secondsOnGate()
                });

                callback(values.rememberMe !== false);
              }}
            >
              yes, let me in!
            </Button>
          </Box>
        </Flex>

        <Flex
          justifyContent="center"
          alignItems="center"
          css={{
            gap: "8px",
            "@media screen and (max-width:769px)": {
              marginTop: "6px"
            }
          }}
        >
          <input
            id="rememberMe"
            name="rememberMe"
            type="checkbox"
            checked={values.rememberMe}
            onChange={handleChange}
            style={{ width: "16px", height: "16px", accentColor: "#3041FF" }}
          />
          <label htmlFor="rememberMe">
            <Text
              fontSize={1}
              color="#141313"
              fontFamily="Source Code Pro"
              css={{
                "@media screen and (max-width:769px)": {
                  fontSize: "13px"
                }
              }}
            >
              Remember me for 30 days
            </Text>
          </label>
        </Flex>
      </form>
    );
  }
}

export default withFormik({
  // Transform outer props into form values
  mapPropsToValues: props => ({
    DAY: "",
    MONTH: "",
    YEAR: "",
    rememberMe: true,
    COUNTRY:
      props.ipData &&
      (props.ipData.country_code === "CA" || props.ipData.country_code === "US")
        ? props.ipData.country_code
        : "OT",
    PROVINCE:
      props.ipData && props.ipData.country_code === "CA"
        ? props.ipData.region_code
        : ""
  }),

  handleSubmit: (values, { setSubmitting, setStatus, props }) => {
    // Set submitting to true
    setSubmitting(true);
  }
})(AgeGateForm);
